import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2, Sparkles, X } from "lucide-react";
import api, { formatApiError, API } from "../lib/api";
import { toast } from "sonner";

const MAX_RECORDING_MS = 90_000;  // 90s cap — voice memos for tasks should be quick

/**
 * VoiceCapture
 *
 * Self-contained mic capture button. Records audio via MediaRecorder, posts
 * to /api/tasks/voice, and invokes `onParsed({ transcript, parsed })` so the
 * parent can autofill its form.
 *
 * Props:
 *  - onParsed({ transcript, parsed })  → required
 *  - onTranscript(text)                → optional (called as soon as STT returns,
 *                                        before parse complete; useful for UX)
 *  - accentColor                        → workspace accent
 *  - size?: 'sm'|'md'                   → default 'md'
 *  - testIdPrefix?: string              → default 'voice'
 */
export default function VoiceCapture({
  onParsed,
  onTranscript,
  accentColor = "#0281FF",
  size = "md",
  testIdPrefix = "voice",
}) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const stopTimerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try { mediaRecorderRef.current?.stop(); } catch {}
      if (tickRef.current) clearInterval(tickRef.current);
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    };
  }, []);

  const start = async () => {
    if (recording || processing) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone capture isn't supported by this browser");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch (e) {
      toast.error(e?.message || "Microphone permission denied");
      return;
    }

    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
      "audio/ogg;codecs=opus",
    ];
    const supported =
      candidates.find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
    const mr = new MediaRecorder(stream, supported ? { mimeType: supported } : undefined);
    chunksRef.current = [];
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      if (tickRef.current) clearInterval(tickRef.current);
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      tickRef.current = null;
      stopTimerRef.current = null;
      setRecording(false);
      setElapsed(0);

      const cleanMime = (mr.mimeType || "audio/webm").split(";")[0];
      const ext = cleanMime.split("/")[1] || "webm";
      const blob = new Blob(chunksRef.current, { type: cleanMime });
      if (!blob.size) {
        toast.error("No audio captured — try again");
        return;
      }

      setProcessing(true);
      try {
        const fd = new FormData();
        fd.append("audio", blob, `voice.${ext}`);
        const { data } = await api.post("/tasks/voice", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60_000,
        });
        if (data?.transcript) {
          onTranscript?.(data.transcript);
          if (data.parsed && Object.keys(data.parsed).length > 0) {
            onParsed?.(data);
            toast.success(`Voice → task ready (${data.parsed.title || "untitled"})`);
          } else {
            // Fallback: at least put the raw transcript in
            onParsed?.({ transcript: data.transcript, parsed: { title: data.transcript } });
            toast.success("Transcribed — review the fields");
          }
        } else {
          toast.message("Couldn't hear anything — try again");
        }
      } catch (e) {
        const detail = formatApiError(e.response?.data?.detail);
        toast.error(detail || "Voice processing failed");
      } finally {
        setProcessing(false);
      }
    };

    mediaRecorderRef.current = mr;
    mr.start();
    setRecording(true);
    setElapsed(0);
    const startedAt = Date.now();
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    stopTimerRef.current = setTimeout(() => {
      try { mr.stop(); } catch {}
      toast.message("Hit the 90s cap — wrapping up");
    }, MAX_RECORDING_MS);
  };

  const stop = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
  };

  const cancel = () => {
    try {
      chunksRef.current = []; // discard
      mediaRecorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop());
      mediaRecorderRef.current?.stop();
    } catch {}
    setRecording(false);
    setProcessing(false);
    setElapsed(0);
    if (tickRef.current) clearInterval(tickRef.current);
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
  };

  const padded = (n) => String(n).padStart(2, "0");
  const elapsedLabel = `${padded(Math.floor(elapsed / 60))}:${padded(elapsed % 60)}`;
  const cls = size === "sm"
    ? "h-9 w-9 rounded-md"
    : "h-10 w-10 rounded-md";

  if (processing) {
    return (
      <button
        type="button"
        disabled
        data-testid={`${testIdPrefix}-processing`}
        className={`${cls} inline-flex items-center justify-center bg-secondary text-foreground border border-border cursor-wait`}
        title="Transcribing and parsing…"
      >
        <Loader2 size={15} className="animate-spin" style={{ color: accentColor }} />
      </button>
    );
  }

  if (recording) {
    return (
      <div className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-recording`}>
        <button
          type="button"
          onClick={stop}
          data-testid={`${testIdPrefix}-stop-btn`}
          className={`${cls} relative inline-flex items-center justify-center bg-red-500 text-white animate-pulse`}
          title="Stop and transcribe"
        >
          <Square size={13} />
        </button>
        <span className="text-[11px] font-mono text-red-500 tabular-nums">
          {elapsedLabel}
        </span>
        <button
          type="button"
          onClick={cancel}
          data-testid={`${testIdPrefix}-cancel-btn`}
          className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
          title="Cancel recording"
        >
          <X size={13} />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      data-testid={`${testIdPrefix}-start-btn`}
      className={`${cls} inline-flex items-center justify-center text-white shadow-sm hover:opacity-90 active:scale-95 transition-all`}
      style={{ backgroundColor: accentColor }}
      title="Hold space-mic & speak — Whisper STT will fill the task"
    >
      <Mic size={15} strokeWidth={2} />
    </button>
  );
}
